/**
 * ProductSelector — high-variant safe, Liquid-authoritative.
 *
 * Never reads product.variants (Liquid caps it at 250) and never decides which
 * media to show. A selection is applied to the DOM immediately so the click
 * always sticks, then a Section Rendering request returns markup Liquid has
 * already resolved: availability, the variant, the price, and the gallery with
 * its active slide marked. The JS swaps that in.
 *
 * Re-entrancy matters: syncing the picker and the product bar dispatches
 * `change` on selects and inputs, and those bubble straight back into onChange.
 * Every programmatic update runs inside `mutating()` so the handler ignores its
 * own echoes.
 */
class ProductSelector extends HTMLElement {
  connectedCallback() {
    if (this.initialised) return;
    this.initialised = true;

    this.sectionId = this.dataset.sectionId;
    this.productUrl = this.dataset.url;
    this.controller = null;
    this.isMutating = false;

    this.form =
      document.querySelector(this.dataset.formSelector || '') ||
      this.closest('form') ||
      document.querySelector(`#ProductForm-${this.sectionId}`);

    this.productBar = this.dataset.productBarId
      ? document.getElementById(this.dataset.productBarId)
      : null;

    this.onChange = this.onChange.bind(this);
    this.addEventListener('change', this.onChange);
    if (this.productBar) this.productBar.addEventListener('change', this.onChange);

    this.applyVariantState();
  }

  /** Run a DOM update without the resulting change events re-entering onChange. */
  mutating(fn) {
    this.isMutating = true;
    try {
      fn();
    } finally {
      this.isMutating = false;
    }
  }

  /* ---------------------------------------------------------------- surfaces */

  get surfaces() {
    return [this, this.productBar].filter(Boolean);
  }

  wrappersIn(root) {
    return [...root.querySelectorAll('[data-input-wrapper]')];
  }

  get selection() {
    return this.wrappersIn(this).map(wrapper => {
      const radio = wrapper.querySelector('input[type="radio"]:checked');
      if (radio) return radio.dataset.optionValueId || null;

      const select = wrapper.querySelector('select');
      if (select) return select.selectedOptions[0]?.dataset.optionValueId || null;

      return null;
    });
  }

  /* ------------------------------------------------------------------ events */

  onChange(event) {
    if (this.isMutating) return;

    const el = event.target;
    if (!el || el.type === 'number') return;

    const valueId =
      el.tagName === 'SELECT'
        ? el.selectedOptions[0]?.dataset.optionValueId
        : el.dataset.optionValueId;
    if (!valueId) return;

    const wrapper = el.closest('[data-input-wrapper]');
    const root = el.closest('product-selector') || this.productBar;
    if (!wrapper || !root) return;

    const index = this.wrappersIn(root).indexOf(wrapper);
    if (index === -1) return;

    const next = this.selection;
    next[index] = valueId;

    this.applySelection(next);
    this.render(next);
  }

  /* ------------------------------------------------------- local application */

  applySelection(ids) {
    this.mutating(() => {
      this.surfaces.forEach(root => {
        this.wrappersIn(root).forEach((wrapper, index) => {
          const id = ids[index];
          if (!id) return;

          const el = wrapper.querySelector(
            `[data-option-value-id="${CSS.escape(id)}"]`
          );
          if (!el) return;

          if (el.tagName === 'OPTION') {
            const select = el.closest('select');
            if (select) select.value = el.value;
          } else {
            el.checked = true;
          }
        });

        root.querySelectorAll('dropdown-input').forEach(dropdown => {
          if (typeof dropdown.update === 'function') dropdown.update();
        });
      });

      this.refreshActiveStates();
    });
  }

  refreshActiveStates() {
    this.querySelectorAll('input[type="radio"]').forEach(input => {
      const label = document.querySelector(`label[for="${CSS.escape(input.id)}"]`);
      if (label) label.classList.toggle('is-active', input.checked);
    });

    this.wrappersIn(this).forEach(wrapper => {
      const output = wrapper.querySelector('[data-selected-option-value]');
      if (!output) return;

      const radio = wrapper.querySelector('input[type="radio"]:checked');
      const select = wrapper.querySelector('select');
      const value = radio ? radio.value : select?.value;
      if (value) output.textContent = value;
    });
  }

  /* ---------------------------------------------------------------- fetching */

  render(ids) {
    const params = new URLSearchParams(window.location.search);
    params.delete('variant');
    params.set('section_id', this.sectionId);
    params.set('option_values', ids.filter(Boolean).join(','));

    if (this.controller) this.controller.abort();
    this.controller = new AbortController();
    this.setLoading(true);

    return fetch(`${this.productUrl}?${params.toString()}`, {
      signal: this.controller.signal
    })
      .then(response => response.text())
      .then(text => {
        const html = new DOMParser().parseFromString(text, 'text/html');
        const source =
          html.getElementById(this.id) || html.querySelector('product-selector');
        if (!source) return;

        this.mutating(() => {
          this.adoptVariantData(source);
          this.adoptOptionValueState(source, ids);
          this.replaceGallery(html);
          this.adoptRegions(html);
        });

        this.applyVariantState();
      })
      .catch(error => {
        if (error.name === 'AbortError') return;
        console.error('[product-selector] render failed', error);
      })
      .finally(() => this.setLoading(false));
  }

  setLoading(isLoading) {
    this.classList.toggle('is-loading', isLoading);
    this.setAttribute('aria-busy', isLoading ? 'true' : 'false');
  }

  /* ---------------------------------------------------------------- adopting */

  adoptVariantData(source) {
    ['variantId', 'variantAvailable'].forEach(key => {
      if (source.dataset[key]) {
        this.dataset[key] = source.dataset[key];
      } else {
        delete this.dataset[key];
      }
    });
  }

  adoptOptionValueState(source, ids) {
    const selected = new Set(ids.filter(Boolean));
    const availability = new Map();

    source.querySelectorAll('[data-option-value-id]').forEach(el => {
      availability.set(el.dataset.optionValueId, el.dataset.available === 'true');
    });

    this.surfaces.forEach(root => {
      root.querySelectorAll('[data-option-value-id]').forEach(el => {
        const id = el.dataset.optionValueId;
        if (!availability.has(id)) return;

        const isAvailable = availability.get(id) || selected.has(id);
        el.dataset.available = isAvailable ? 'true' : 'false';
        el.disabled = !isAvailable;

        const label = el.id
          ? document.querySelector(`label[for="${CSS.escape(el.id)}"]`)
          : null;
        if (label) label.classList.toggle('is-disabled', !isAvailable);
      });
    });
  }

  /**
   * Replace the gallery with the server-rendered one. Liquid decides the active
   * slide, so no media id is computed here — it's only read back out of the
   * markup to drive Swiper, which ignores initialSlide on a gallery this large.
   */
  replaceGallery(html) {
    const source = html.querySelector('product-media');
    const destination = document.querySelector('product-media');
    if (!source || !destination) return;

    const nextActive = source.querySelector('[data-selected]')?.dataset.mediaId;
    const currentActive = destination.querySelector('[data-selected]')?.dataset
      .mediaId;
    if (nextActive && nextActive === currentActive) return;

    const next = document.importNode(source, true);
    destination.replaceWith(next);

    if (!nextActive) return;
    requestAnimationFrame(() => {
      if (typeof next.setActiveMedia === 'function') {
        next.setActiveMedia(Number(nextActive));
      }
    });
  }

  adoptRegions(html) {
    html.querySelectorAll('[data-variant-region]').forEach(source => {
      const key = source.dataset.variantRegion;
      const destination = document.querySelector(
        `[data-variant-region="${CSS.escape(key)}"]`
      );
      if (!destination) return;
      destination.classList.remove('visibility-hidden');
      destination.innerHTML = source.innerHTML;
    });
  }

  /* ------------------------------------------------------------- state apply */

  applyVariantState() {
    const variantId = this.dataset.variantId || null;
    const isAvailable = this.dataset.variantAvailable === 'true';

    if (this.form) {
      this.form.removeAttribute('data-has-selling-plan');
      if (typeof this.form.handleErrorMessage === 'function') {
        this.form.handleErrorMessage();
      }
    }

    if (!variantId) {
      this.setUnavailable();
      this.updatePickupAvailability(false);
      return;
    }

    if (this.form && typeof this.form.toggleAddButton === 'function') {
      this.form.toggleAddButton(
        !isAvailable,
        isAvailable ? '' : window.variantStrings?.soldOut || ''
      );
    }

    this.updateVariantInput(variantId);
    this.updateURL(variantId);
    this.updatePickupAvailability(isAvailable);
  }

  updateVariantInput(variantId) {
    this.mutating(() => {
      this.querySelectorAll('input[name="id"]').forEach(input => {
        input.value = variantId;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });

      const installments = document.getElementById(
        this.dataset.installmentsFormId || ''
      );
      const installmentsInput = installments?.querySelector('input[name="id"]');
      if (installmentsInput) {
        installmentsInput.value = variantId;
        installmentsInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
    try {
      document.dispatchEvent(new CustomEvent('variant:change', {
        bubbles: true,
        detail: { variant: { id: variantId } }
      }));
    } catch (err) {}
    try {
      var swat = window.__swat;
      if (swat && swat.evtLayer && swat.JSEvents && swat.JSEvents.variantChanged) {
        var swymEvt = new CustomEvent(swat.JSEvents.variantChanged, {
          detail: { d: { variant: { id: variantId } } }
        });
        swat.evtLayer.dispatchEvent(swymEvt);
      }
    } catch (err) {}
  }

  updateURL(variantId) {
    const params = new URLSearchParams(window.location.search);
    params.set('variant', variantId);
    params.delete('option_values');
    params.delete('section_id');
    window.history.replaceState({}, '', `${this.productUrl}?${params.toString()}`);
  }

  updatePickupAvailability(isAvailable) {
    const node = document.querySelector('pickup-availability');
    if (!node) return;

    const variantId = this.dataset.variantId;
    if (!variantId || !isAvailable) {
      node.removeAttribute('available');
      node.innerHTML = '';
      return;
    }

    node.dataset.variantId = variantId;
    node.setAttribute('available', '');
    if (typeof node.fetchAvailability === 'function') {
      node.fetchAvailability(variantId);
    }
  }

  setUnavailable() {
    if (this.form && typeof this.form.toggleAddButton === 'function') {
      this.form.toggleAddButton(true, '');
    }
    const submitButton = this.form?.submitButton;
    if (submitButton && window.variantStrings?.unavailable) {
      submitButton.textContent = window.variantStrings.unavailable;
    }
  }
}

customElements.define('product-selector', ProductSelector);
